import { usableAuditFilter } from './filterRules';
import { AUDIT_FILTER_MAX_LENGTH, isMetadataPath } from './filterValidation';
import { isRecord } from './mappers';
import type { AuditLogFilters } from './types';
import { type AuditValue, auditValueFilterText, toAuditValue } from './values';

/** The backend matches request-data paths of at most five segments. */
const FLATTEN_MAX_DEPTH = 5;
const FLATTEN_MAX_LEAVES = 100;

export interface JsonLeaf {
    /** Dotted, as `metadataPath` takes it; array elements of objects add their index. */
    path: string;
    value: AuditValue;
    /** Null when the backend cannot match this path, such as a key with a space. */
    filterPath: string | null;
    /** Null for values no equality filter can match: empty, hidden, containers. */
    filterValue: string | null;
    /** True for one element of an array of scalars, which is listed under the array's path. */
    arrayElement: boolean;
}

export type FlattenedJson =
    /** The write path replaced request data over its size cap with this marker. */
    | { kind: 'truncated'; length: number | null }
    | { kind: 'leaves'; leaves: JsonLeaf[]; omitted: number };

function isScalar(value: unknown): boolean {
    return value === null || ['string', 'number', 'boolean'].includes(typeof value);
}

function leaf(path: string[], raw: unknown, arrayElement: boolean): JsonLeaf {
    const value = toAuditValue(raw);
    const dotted = path.join('.');
    const filterValue = auditValueFilterText(value);
    return {
        path: dotted,
        value,
        filterPath: isMetadataPath(dotted) ? dotted : null,
        filterValue:
            filterValue !== null && filterValue.length <= AUDIT_FILTER_MAX_LENGTH.value
                ? filterValue
                : null,
        arrayElement,
    };
}

function isEmptyContainer(value: unknown): boolean {
    if (Array.isArray(value)) return value.length === 0;
    return isRecord(value) && Object.keys(value).length === 0;
}

/** "0 items" or "empty", so the list names the path the raw JSON shows, with no button. */
function emptyLeaf(path: string[], value: unknown): JsonLeaf {
    const shown: AuditValue = Array.isArray(value)
        ? { kind: 'items', count: 0 }
        : { kind: 'empty' };
    return { ...leaf(path, value, false), value: shown, filterValue: null };
}

function collect(value: unknown, path: string[], out: JsonLeaf[]): void {
    if (isEmptyContainer(value)) {
        out.push(emptyLeaf(path, value));
        return;
    }
    const container = isRecord(value) || Array.isArray(value);
    if (container && path.length >= FLATTEN_MAX_DEPTH) {
        out.push(leaf(path, value, false));
        return;
    }
    if (isRecord(value)) {
        for (const [key, child] of Object.entries(value)) collect(child, [...path, key], out);
        return;
    }
    if (!Array.isArray(value)) {
        out.push(leaf(path, value, false));
        return;
    }
    // A request that created pens 901 and 902 stores them as one array, so each id is its
    // own row, and its filter finds the entries whose array there contains it.
    if (value.every(isScalar)) {
        for (const item of value) out.push(leaf(path, item, true));
        return;
    }
    for (const [index, child] of value.entries()) collect(child, [...path, String(index)], out);
}

/**
 * Every scalar leaf of an entry's request data, for the details dialog. A container at
 * the fifth level is listed as a leaf of its own, so what lies deeper is marked rather
 * than lost, and so is an empty one, so the list agrees with the raw JSON. Past 100
 * leaves the rest are counted, not listed.
 */
export function flattenJson(json: Record<string, unknown>): FlattenedJson {
    if (json.truncated === true) {
        const { length } = json;
        return { kind: 'truncated', length: typeof length === 'number' ? length : null };
    }
    const all: JsonLeaf[] = [];
    for (const [key, value] of Object.entries(json)) collect(value, [key], all);
    return {
        kind: 'leaves',
        leaves: all.slice(0, FLATTEN_MAX_LEAVES),
        omitted: Math.max(0, all.length - FLATTEN_MAX_LEAVES),
    };
}

/**
 * What a leaf's button filters by. For one element of a list the path is the list's, and
 * the backend matches a list there that contains the value as well as the value itself.
 */
export function jsonLeafFilter(leaf: JsonLeaf): AuditLogFilters | null {
    if (leaf.filterPath === null || leaf.filterValue === null) return null;
    return usableAuditFilter({ metadataPath: leaf.filterPath, metadataValue: leaf.filterValue });
}
